import { useEffect, useState, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { History, X, Plus, Trash2, Image, Link2, ExternalLink, Upload, ScanEye } from 'lucide-react';
import PostPreviewPanel from '../components/PostPreviewPanel';
import {
  fetchPosts, createPost, updatePost, fetchRevisions, restoreRevision,
  uploadMedia, uploadMediaFromUrl, fetchMedia,
  Post, PostRevision, ContentSection, Faq, InternalLink, MediaObject,
} from '../lib/api';

/* ── helpers ─────────────────────────────────────────────────────── */
function tsToDatetimeLocal(ts?: number) {
  if (!ts) return '';
  const d = new Date(ts * 1000);
  const p = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth()+1)}-${p(d.getDate())}T${p(d.getHours())}:${p(d.getMinutes())}`;
}
function datetimeLocalToTs(v: string) {
  return v ? Math.floor(new Date(v).getTime() / 1000) : undefined;
}
function autoSlug(t: string) {
  return t.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
}
function sectionsToBody(sections: ContentSection[]) {
  return sections.map(s => {
    let out = '';
    if (s.heading) out += `\n## ${s.heading}\n`;
    if (s.body) out += `\n${s.body}\n`;
    if (s.image_url) out += `\n![Image](${s.image_url})\n`;
    return out;
  }).join('\n');
}
function faqsToBody(faqs: Faq[]) {
  return faqs.filter(f => f.q || f.a).map(f => `\n### ${f.q}\n\n${f.a}\n`).join('\n');
}

const BLANK_SECTION: ContentSection = { heading: '', body: '', image_url: '' };
const BLANK_FAQ: Faq = { q: '', a: '' };
const inputCls = 'w-full px-4 py-2 rounded-xl border border-gray-300 bg-white dark:bg-gray-800 dark:border-gray-600 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500';
const labelCls = 'block text-sm font-medium mb-1.5 text-gray-700 dark:text-gray-300';

/* ── Image Picker Modal ──────────────────────────────────────────── */
function ImagePickerModal({ onSelect, onClose }: { onSelect: (url: string) => void; onClose: () => void }) {
  const [media, setMedia] = useState<MediaObject[]>([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [urlInput, setUrlInput] = useState('');
  const fileRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    fetchMedia().then(m => { setMedia(m); setLoading(false); }).catch(() => setLoading(false));
  }, []);

  const [urlError, setUrlError] = useState('');

  const handleUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const result = await uploadMedia(file);
      setMedia(prev => [{ key: result.key, url: result.url, size: file.size, uploaded: null }, ...prev]);
      onSelect(result.url);
    } catch { alert('Upload failed. Check connection and try again.'); }
    finally { setUploading(false); }
  };

  const handleUrlUpload = async () => {
    const src = urlInput.trim();
    if (!src) return;
    setUrlError('');
    setUploading(true);
    try {
      const result = await uploadMediaFromUrl(src);
      setMedia(prev => [{ key: result.key, url: result.url, size: 0, uploaded: null }, ...prev]);
      onSelect(result.url);
    } catch (err: any) {
      setUrlError(err?.message || 'Could not fetch image from URL');
    } finally { setUploading(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-2xl w-full max-w-3xl max-h-[85vh] flex flex-col">
        <div className="flex items-center justify-between p-5 border-b border-gray-200 dark:border-gray-700">
          <h2 className="text-lg font-bold text-gray-900 dark:text-gray-100">Select Image</h2>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700"><X size={18} /></button>
        </div>
        <div className="p-4 border-b border-gray-200 dark:border-gray-700 space-y-3">
          {/* Upload from device */}
          <div>
            <p className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1.5">Upload from device</p>
            <input ref={fileRef} type="file" accept="image/*" onChange={handleUpload} className="hidden" />
            <button
              onClick={() => fileRef.current?.click()}
              disabled={uploading}
              className="flex items-center gap-2 px-4 py-2 bg-emerald-600 text-white rounded-xl hover:bg-emerald-700 text-sm font-medium disabled:opacity-50 transition"
            >
              <Upload size={16} /> {uploading ? 'Uploading…' : 'Choose Image File'}
            </button>
          </div>
          {/* Upload from URL */}
          <div>
            <p className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1.5">Upload from URL (saves to your media library)</p>
            <div className="flex gap-2">
              <input
                value={urlInput} onChange={e => { setUrlInput(e.target.value); setUrlError(''); }}
                onKeyDown={e => e.key === 'Enter' && handleUrlUpload()}
                placeholder="https://example.com/image.jpg" className={`${inputCls} flex-1`}
                disabled={uploading}
              />
              <button
                onClick={handleUrlUpload}
                disabled={uploading || !urlInput.trim()}
                className="px-4 py-2 bg-blue-600 text-white rounded-xl hover:bg-blue-700 text-sm font-medium disabled:opacity-50 transition whitespace-nowrap"
              >{uploading ? 'Uploading…' : 'Upload URL'}</button>
            </div>
            {urlError && <p className="text-xs text-red-500 mt-1">{urlError}</p>}
          </div>
        </div>
        <div className="flex-1 overflow-y-auto p-4">
          {loading ? <p className="text-center text-gray-400 py-8">Loading media…</p> : media.length === 0 ? (
            <p className="text-center text-gray-400 py-8">No images uploaded yet. Upload one above.</p>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
              {media.map(obj => (
                <button key={obj.key} onClick={() => onSelect(obj.url)}
                  className="group relative aspect-square rounded-xl overflow-hidden border-2 border-transparent hover:border-blue-500 transition bg-gray-100 dark:bg-gray-800">
                  <img src={obj.url} alt={obj.key} className="w-full h-full object-cover" onError={e => (e.currentTarget.style.display = 'none')} />
                  <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition flex items-end p-2">
                    <span className="text-white text-xs truncate opacity-0 group-hover:opacity-100 transition">{obj.key}</span>
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/* ── Internal Link Picker ────────────────────────────────────────── */
function LinkPickerModal({ selected, onDone, onClose }: {
  selected: InternalLink[];
  onDone: (links: InternalLink[]) => void;
  onClose: () => void;
}) {
  const [posts, setPosts] = useState<Post[]>([]);
  const [loading, setLoading] = useState(true);
  const [picked, setPicked] = useState<InternalLink[]>(selected);

  useEffect(() => {
    fetchPosts({ status: 'published' })
      .then(p => { setPosts(p); setLoading(false); })
      .catch(() => setLoading(false));
  }, []);

  const toggle = (post: Post) => {
    const exists = picked.find(l => l.slug === post.slug);
    if (exists) {
      setPicked(picked.filter(l => l.slug !== post.slug));
    } else if (picked.length < 5) {
      setPicked([...picked, { slug: post.slug, label: post.title }]);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-2xl w-full max-w-lg max-h-[80vh] flex flex-col">
        <div className="flex items-center justify-between p-5 border-b border-gray-200 dark:border-gray-700">
          <div>
            <h2 className="text-lg font-bold text-gray-900 dark:text-gray-100">Select Internal Links</h2>
            <p className="text-xs text-gray-400 mt-0.5">Pick up to 5 published posts ({picked.length}/5 selected)</p>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700"><X size={18} /></button>
        </div>
        <div className="flex-1 overflow-y-auto p-4 space-y-2">
          {loading ? <p className="text-center text-gray-400 py-8">Loading posts…</p> : posts.map(post => {
            const isSelected = picked.some(l => l.slug === post.slug);
            return (
              <button key={post.id} onClick={() => toggle(post)}
                className={`w-full text-left px-4 py-3 rounded-xl border-2 transition ${isSelected ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20' : 'border-gray-200 dark:border-gray-700 hover:border-blue-300'}`}
              >
                <div className="flex items-start justify-between gap-2">
                  <div>
                    <p className="text-sm font-medium text-gray-900 dark:text-gray-100">{post.title}</p>
                    <p className="text-xs text-gray-400">/{post.slug}/</p>
                  </div>
                  {isSelected && <span className="text-blue-500 text-xs font-semibold shrink-0">✓ selected</span>}
                </div>
              </button>
            );
          })}
        </div>
        <div className="p-4 border-t border-gray-200 dark:border-gray-700 flex justify-end gap-3">
          <button onClick={onClose} className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-xl text-sm hover:bg-gray-50 dark:hover:bg-gray-800">Cancel</button>
          <button onClick={() => { onDone(picked); onClose(); }} className="px-4 py-2 bg-blue-600 text-white rounded-xl text-sm font-medium hover:bg-blue-700">
            Apply ({picked.length})
          </button>
        </div>
      </div>
    </div>
  );
}

/* ── Markdown Toolbar ────────────────────────────────────────────── */
function MarkdownToolbar({ textareaRef, value, onChange, onPickImage }: {
  textareaRef: React.RefObject<HTMLTextAreaElement>;
  value: string;
  onChange: (v: string) => void;
  onPickImage?: () => void;
}) {
  const wrap = (before: string, after = before) => {
    const el = textareaRef.current;
    if (!el) return;
    const { selectionStart: s, selectionEnd: e } = el;
    const selected = value.slice(s, e);
    const next = value.slice(0, s) + before + selected + after + value.slice(e);
    onChange(next);
    setTimeout(() => { el.focus(); el.setSelectionRange(s + before.length, e + before.length); }, 0);
  };
  const insertLine = (prefix: string) => {
    const el = textareaRef.current;
    if (!el) return;
    const { selectionStart: s } = el;
    const lineStart = value.lastIndexOf('\n', s - 1) + 1;
    const lineEnd = value.indexOf('\n', s);
    const end = lineEnd === -1 ? value.length : lineEnd;
    const line = value.slice(lineStart, end);
    const next = value.slice(0, lineStart) + prefix + line + value.slice(end);
    onChange(next);
    setTimeout(() => { el.focus(); el.setSelectionRange(lineStart + prefix.length, lineStart + prefix.length + line.length); }, 0);
  };
  const insertAtCursor = (text: string) => {
    const el = textareaRef.current;
    if (!el) return;
    const s = el.selectionStart;
    const next = value.slice(0, s) + text + value.slice(s);
    onChange(next);
    setTimeout(() => { el.focus(); el.setSelectionRange(s + text.length, s + text.length); }, 0);
  };

  const btns = [
    { label: 'H1', action: () => insertLine('# '), title: 'Heading 1' },
    { label: 'H2', action: () => insertLine('## '), title: 'Heading 2' },
    { label: 'H3', action: () => insertLine('### '), title: 'Heading 3' },
    { label: 'B', action: () => wrap('**'), title: 'Bold', cls: 'font-bold' },
    { label: 'I', action: () => wrap('*'), title: 'Italic', cls: 'italic' },
    { label: '`', action: () => wrap('`'), title: 'Inline code', cls: 'font-mono' },
    { label: '```', action: () => wrap('\n```\n', '\n```\n'), title: 'Code block', cls: 'font-mono text-xs' },
    { label: '—', action: () => { const el = textareaRef.current; if (!el) return; const s = el.selectionStart; onChange(value.slice(0,s)+'\n---\n'+value.slice(s)); }, title: 'Horizontal rule' },
    { label: '🔗', action: () => wrap('[', '](url)'), title: 'Link' },
  ];

  return (
    <div className="flex flex-wrap gap-1 mb-2">
      {btns.map(b => (
        <button key={b.label} type="button" onClick={b.action} title={b.title}
          className={`px-2.5 py-1 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 text-sm transition ${b.cls || ''}`}>
          {b.label}
        </button>
      ))}
      {/* Image button — opens picker if callback provided, else inserts placeholder */}
      <button
        type="button"
        title="Insert Image"
        onClick={() => {
          if (onPickImage) {
            onPickImage();
          } else {
            insertAtCursor('![Image](url)');
          }
        }}
        className="px-2.5 py-1 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 text-sm transition flex items-center gap-1"
      >
        <Image size={13} /> 📷
      </button>
    </div>
  );
}

/* ── Per-paragraph block: toolbar + textarea + image (upload / gallery) ── */
function ParagraphBlock({
  index, text, imageUrl, showDelete,
  onTextChange, onImageChange, onBrowseImage, onRemove,
}: {
  index: number; text: string; imageUrl: string; showDelete: boolean;
  onTextChange: (v: string) => void; onImageChange: (v: string) => void;
  onBrowseImage: () => void; onRemove: () => void;
}) {
  const taRef = useRef<HTMLTextAreaElement>(null);
  return (
    <div className="rounded-xl border border-gray-200 dark:border-gray-600 p-3 space-y-2 bg-white dark:bg-gray-800/60">
      {/* header row – always show index so user knows which block they're in */}
      <div className="flex items-center justify-between">
        <span className="text-xs font-medium text-gray-400 dark:text-gray-500 uppercase tracking-wide">
          Paragraph {index + 1}
        </span>
        {showDelete && (
          <button type="button" onClick={onRemove}
            className="flex items-center gap-1 text-xs text-red-400 hover:text-red-600 transition">
            <Trash2 size={12} /> Remove
          </button>
        )}
      </div>

      {/* markdown toolbar */}
      <MarkdownToolbar textareaRef={taRef} value={text} onChange={onTextChange} />

      {/* text area */}
      <textarea
        ref={taRef}
        value={text}
        onChange={e => onTextChange(e.target.value)}
        rows={4}
        className={inputCls}
        placeholder="Paragraph content…"
      />

      {/* image row */}
      <div>
        <label className="block text-xs font-medium mb-1 text-gray-500 dark:text-gray-400">
          Paragraph Image <span className="font-normal">(optional)</span>
        </label>
        <div className="flex gap-2">
          <input
            value={imageUrl}
            onChange={e => onImageChange(e.target.value)}
            className={`${inputCls} flex-1 text-sm py-1.5`}
            placeholder="https://… or browse / upload"
          />
          <button
            type="button"
            onClick={onBrowseImage}
            className="flex items-center gap-1.5 px-3 py-1.5 bg-gray-50 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-xl text-sm hover:bg-gray-100 dark:hover:bg-gray-600 transition whitespace-nowrap"
          >
            <Image size={15} /> Browse
          </button>
        </div>
        {imageUrl && (
          <div className="mt-2 flex items-start gap-2">
            <img src={imageUrl} alt="" className="max-h-28 rounded-lg object-cover border border-gray-200 dark:border-gray-600" />
            <button type="button" onClick={() => onImageChange('')}
              className="text-red-400 hover:text-red-600 mt-1 transition" title="Remove image">
              <X size={14} />
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

/* ── Multi-paragraph editor — per-section, manages entries + image picker ── */
interface ParaEntry { text: string; imageUrl: string; }

/** Serialize entries → markdown body string stored in ContentSection.body */
function serializeEntries(entries: ParaEntry[]): string {
  return entries.map(e => e.imageUrl ? `${e.text}\n\n![Image](${e.imageUrl})` : e.text).join('\n\n');
}

/** Best-effort parse of existing body string back into entries.
 *  Consecutive image-only lines are attached to the preceding text block. */
function parseEntries(body: string): ParaEntry[] {
  if (!body || !body.trim()) return [{ text: '', imageUrl: '' }];
  // Split on double-newline
  const blocks = body.split(/\n\n+/);
  const entries: ParaEntry[] = [];
  let pending: ParaEntry = { text: '', imageUrl: '' };
  for (const block of blocks) {
    const imgMatch = block.trim().match(/^!\[.*?\]\((.*?)\)$/);
    if (imgMatch) {
      // Attach image to pending paragraph
      pending.imageUrl = imgMatch[1];
      entries.push(pending);
      pending = { text: '', imageUrl: '' };
    } else {
      if (pending.text || pending.imageUrl) {
        entries.push(pending);
      }
      pending = { text: block, imageUrl: '' };
    }
  }
  if (pending.text || pending.imageUrl) entries.push(pending);
  return entries.length > 0 ? entries : [{ text: '', imageUrl: '' }];
}

function SectionParagraphEditor({
  body, onChange,
}: {
  body: string; onChange: (v: string) => void;
}) {
  const [entries, setEntries] = useState<ParaEntry[]>(() => parseEntries(body));
  const [imagePickerIdx, setImagePickerIdx] = useState<number | null>(null);

  const mutate = (fn: (prev: ParaEntry[]) => ParaEntry[]) =>
    setEntries(prev => { const next = fn(prev); onChange(serializeEntries(next)); return next; });

  const updateText  = (i: number, text: string)     => mutate(e => { const n=[...e]; n[i]={...n[i],text}; return n; });
  const updateImage = (i: number, imageUrl: string) => mutate(e => { const n=[...e]; n[i]={...n[i],imageUrl}; return n; });
  const addEntry    = ()                             => mutate(e => [...e, { text: '', imageUrl: '' }]);
  const removeEntry = (i: number)                   => mutate(e => { const n=e.filter((_,j)=>j!==i); return n.length>0?n:[{text:'',imageUrl:''}]; });

  return (
    <div className="space-y-2">
      <label className={labelCls}>Paragraph{entries.length > 1 ? 's' : ''}</label>
      {entries.map((entry, i) => (
        <ParagraphBlock
          key={i}
          index={i}
          text={entry.text}
          imageUrl={entry.imageUrl}
          showDelete={entries.length > 1}
          onTextChange={v => updateText(i, v)}
          onImageChange={v => updateImage(i, v)}
          onBrowseImage={() => setImagePickerIdx(i)}
          onRemove={() => removeEntry(i)}
        />
      ))}

      <button
        type="button"
        onClick={addEntry}
        className="flex items-center gap-1.5 px-3 py-1.5 w-full justify-center border border-dashed border-gray-300 dark:border-gray-600 rounded-xl text-sm text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700/40 transition"
      >
        <Plus size={14} /> Add Paragraph
      </button>

      {imagePickerIdx !== null && (
        <ImagePickerModal
          onSelect={url => { updateImage(imagePickerIdx, url); setImagePickerIdx(null); }}
          onClose={() => setImagePickerIdx(null)}
        />
      )}
    </div>
  );
}

/* ── Main BlogEditor ─────────────────────────────────────────────── */
function BlogEditor({ postType = 'blog', baseRoute = '/blog' }: { postType?: 'blog' | 'guide'; baseRoute?: string } = {}) {
  const { id } = useParams();
  const navigate = useNavigate();
  const isNew = !id || id === 'new';
  const draftKey = `blog_draft_${id || 'new'}`;

  const [post, setPost] = useState<Partial<Post>>({
    title: '', slug: '', body: '', lang: 'en', status: 'draft',
    meta_description: '', author: 'inbox Me', cover_image_url: '',
    post_type: postType,
    tags: [], sections: [{ ...BLANK_SECTION }], faqs: [BLANK_FAQ, BLANK_FAQ, BLANK_FAQ, BLANK_FAQ, BLANK_FAQ],
    internal_links: [], cta_text: '', cta_button_label: '', cta_button_url: '',
    meta_og_image: '', meta_keywords: '',
  });
  const [tagsInput, setTagsInput] = useState('');
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState('');
  const [fieldErrors, setFieldErrors] = useState<{ title?: boolean; slug?: boolean }>({});
  const [activeTab, setActiveTab] = useState<'content' | 'seo' | 'settings'>('content');
  // Changes when an existing post is loaded so SectionParagraphEditors remount
  // with the correct initial body instead of the empty-string defaults.
  const [loadedAt, setLoadedAt] = useState(0);

  // Auto-save state
  const [draftSaved, setDraftSaved] = useState<Date | null>(null);
  const [hasDraft, setHasDraft] = useState(false);
  const [showRestorePrompt, setShowRestorePrompt] = useState(false);
  const autoSaveRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Restore draft prompt on load
  useEffect(() => {
    const stored = localStorage.getItem(draftKey);
    if (stored) {
      try {
        const d = JSON.parse(stored);
        if (d && d.title !== undefined) {
          setHasDraft(true);
          setShowRestorePrompt(true);
        }
      } catch { /* ignore corrupt draft */ }
    }
  }, [draftKey]); // eslint-disable-line

  // Auto-save every 30 seconds
  useEffect(() => {
    autoSaveRef.current = setInterval(() => {
      if (post.title || post.body || (post.sections || []).some(s => s.body || s.heading)) {
        localStorage.setItem(draftKey, JSON.stringify({ ...post, _tagsInput: tagsInput }));
        setDraftSaved(new Date());
      }
    }, 30_000);
    return () => { if (autoSaveRef.current) clearInterval(autoSaveRef.current); };
  }, [post, tagsInput, draftKey]);

  const restoreDraft = () => {
    const stored = localStorage.getItem(draftKey);
    if (!stored) return;
    try {
      const d = JSON.parse(stored);
      const { _tagsInput, ...restPost } = d;
      setPost(restPost);
      if (_tagsInput) setTagsInput(_tagsInput);
    } catch { /* ignore */ }
    setShowRestorePrompt(false);
    setHasDraft(false);
  };
  const discardDraft = () => {
    localStorage.removeItem(draftKey);
    setShowRestorePrompt(false);
    setHasDraft(false);
  };

  // Image pickers
  const [coverPickerOpen, setCoverPickerOpen] = useState(false);
  const [mobileCoverPickerOpen, setMobileCoverPickerOpen] = useState(false);
  const [sectionImagePicker, setSectionImagePicker] = useState<number | null>(null);
  const [ogImagePickerOpen, setOgImagePickerOpen] = useState(false);
  const [bodyImagePickerOpen, setBodyImagePickerOpen] = useState(false);
  const [linkPickerOpen, setLinkPickerOpen] = useState(false);

  // Revisions
  const [showRevisions, setShowRevisions] = useState(false);
  const [revisions, setRevisions] = useState<PostRevision[]>([]);
  const [loadingRevisions, setLoadingRevisions] = useState(false);
  const [pendingRestore, setPendingRestore] = useState<PostRevision | null>(null);

  // Body textarea ref for toolbar + cursor tracking for image insertion
  const bodyRef = useRef<HTMLTextAreaElement>(null);
  const bodyCursorRef = useRef<number>(0);

  // Load existing post
  useEffect(() => {
    if (!isNew) {
      fetchPosts().then(posts => {
        const p = posts.find(x => x.id === id);
        if (p) {
          // Legacy posts stored headings as plain "# Heading" lines and the
          // section editor rebuilds those. The new format marks headings with
          // "### [H2] ..." and must survive an edit round-trip untouched.
          const cleanBody = (p.body || '')
            .replace(/^#{1,6}\s+(?!\[H[1-6]\])[^\n]*$/gm, '')
            .replace(/\n{3,}/g, '\n\n')
            .trim();
          setPost({ ...p, body: cleanBody });
          setTagsInput((p.tags || []).join(', '));
          if (!p.sections || p.sections.length === 0) {
            setPost(prev => ({ ...prev, sections: [{ ...BLANK_SECTION }] }));
          }
          if (!p.faqs || p.faqs.length === 0) {
            setPost(prev => ({ ...prev, faqs: Array(5).fill(null).map(() => ({ ...BLANK_FAQ })) }));
          }
          // Trigger SectionParagraphEditors to remount with loaded body values
          setLoadedAt(Date.now());
        }
      });
    }
  }, [id, isNew]);

  // Auto-slug on new post when title changes
  const handleTitle = (title: string) => {
    setPost(p => ({ ...p, title, slug: isNew ? autoSlug(title) : p.slug }));
  };

  const openRevisions = async () => {
    if (isNew) return;
    setShowRevisions(true);
    setLoadingRevisions(true);
    setRevisions(await fetchRevisions(id!));
    setLoadingRevisions(false);
  };

  const restore = async (rev: PostRevision) => {
    setPendingRestore(rev);
  };

  const confirmRestore = async () => {
    if (!pendingRestore) return;
    await restoreRevision(pendingRestore.id);
    setPendingRestore(null);
    setShowRevisions(false);
    navigate(baseRoute);
  };

  const save = async () => {
    if (!post.title || !post.slug) {
      setSaveError('Title and slug are required — please fill them in.');
      setFieldErrors({ title: !post.title, slug: !post.slug });
      return;
    }
    setSaving(true);
    setSaveError('');
    setFieldErrors({});
    // Clear draft on explicit save
    localStorage.removeItem(draftKey);
    setDraftSaved(null);
    const tags = tagsInput.split(',').map(t => t.trim()).filter(Boolean);
    // Build combined body from sections + faqs if body is empty
    let body = post.body || '';
    if (!body.trim() && post.sections?.some(s => s.body || s.heading)) {
      body = sectionsToBody(post.sections || []);
      if (post.faqs?.some(f => f.q)) body += '\n\n## FAQ\n' + faqsToBody(post.faqs || []);
    }
    const payload = { ...post, body, tags };
    try {
      let res;
      if (isNew) {
        res = await createPost(payload);
      } else {
        res = await updatePost(id!, payload);
      }
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error((data as any).error || `Server error ${res.status}`);
      }
      navigate(baseRoute);
    } catch (e) {
      setSaveError((e as Error).message || 'Save failed — please try again.');
    } finally {
      setSaving(false);
    }
  };

  /* section helpers */
  const updateSection = (i: number, field: keyof ContentSection, value: string) => {
    setPost(p => {
      const sections = [...(p.sections || [])];
      sections[i] = { ...sections[i], [field]: value };
      return { ...p, sections };
    });
  };
  const addSection = () => setPost(p => ({ ...p, sections: [...(p.sections || []), { ...BLANK_SECTION }] }));
  const removeSection = (i: number) => setPost(p => ({ ...p, sections: (p.sections || []).filter((_, j) => j !== i) }));

  /* faq helpers */
  const updateFaq = (i: number, field: keyof Faq, value: string) => {
    setPost(p => {
      const faqs = [...(p.faqs || [])];
      faqs[i] = { ...faqs[i], [field]: value };
      return { ...p, faqs };
    });
  };
  const addFaq = () => setPost(p => ({ ...p, faqs: [...(p.faqs || []), { ...BLANK_FAQ }] }));

  const tabs = ['content', 'seo', 'settings'] as const;
  const tabLabel = { content: '✏️ Content', seo: '🔍 SEO & Meta', settings: '⚙️ Settings' };

  const [showPreview, setShowPreview] = useState(false);

  return (
    <div>
      {showPreview && (
        <PostPreviewPanel post={post as Parameters<typeof PostPreviewPanel>[0]['post']} onClose={() => setShowPreview(false)} />
      )}
      {/* Draft restore prompt */}
      {showRestorePrompt && (
        <div className="mb-4 flex items-center gap-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-700 rounded-2xl px-4 py-3">
          <span className="text-amber-600 dark:text-amber-400 text-sm font-medium">📝 You have an unsaved draft for this post.</span>
          <button onClick={restoreDraft} className="px-3 py-1.5 bg-amber-600 text-white rounded-xl text-xs font-medium hover:bg-amber-700 transition">Restore Draft</button>
          <button onClick={discardDraft} className="px-3 py-1.5 border border-amber-300 dark:border-amber-600 rounded-xl text-xs text-amber-700 dark:text-amber-400 hover:bg-amber-100 dark:hover:bg-amber-900/40 transition">Discard</button>
        </div>
      )}

      {/* Auto-save indicator */}
      {draftSaved && (
        <div className="mb-4 flex items-center gap-2 text-xs text-gray-400 dark:text-gray-500 bg-gray-50 dark:bg-gray-800/50 rounded-xl px-3 py-2 w-fit">
          <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
          Draft auto-saved at {draftSaved.toLocaleTimeString()}
        </div>
      )}

      {/* Header */}
      <div className="sticky top-0 z-20 flex items-center justify-between mb-6 gap-2 bg-white/95 dark:bg-gray-900/95 backdrop-blur-sm -mx-4 sm:-mx-6 lg:-mx-8 px-4 sm:px-6 lg:px-8 py-3 border-b border-gray-200/60 dark:border-gray-700/60 shadow-sm">
        <h1 className="text-base sm:text-2xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent shrink-0">
          {isNew ? 'New Post' : 'Edit Post'}
        </h1>
        <div className="flex gap-1.5 sm:gap-2 items-center">
          <button onClick={() => setShowPreview(true)}
            title="Preview"
            className="inline-flex items-center gap-1.5 px-2.5 sm:px-3 py-2 rounded-xl bg-violet-100 dark:bg-violet-900/30 text-violet-700 dark:text-violet-300 hover:bg-violet-200 dark:hover:bg-violet-900/50 text-sm font-semibold transition">
            <ScanEye size={15} /> <span className="hidden sm:inline">Preview</span>
          </button>
          {!isNew && (
            <button onClick={openRevisions} title="History" className="inline-flex items-center gap-1.5 px-2.5 sm:px-3 py-2 rounded-xl border border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700 text-sm transition">
              <History size={15} /> <span className="hidden sm:inline">History</span>
            </button>
          )}
          <button onClick={() => navigate(baseRoute)} title="Cancel" className="inline-flex items-center gap-1.5 px-2.5 sm:px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-xl hover:bg-gray-50 dark:hover:bg-gray-700 text-sm transition">
            <X size={15} /> <span className="hidden sm:inline">Cancel</span>
          </button>
          <button onClick={save} disabled={saving} className="inline-flex items-center gap-1.5 px-3 sm:px-4 py-2 bg-gradient-to-r from-blue-500 to-blue-600 text-white rounded-xl hover:from-blue-600 hover:to-blue-700 disabled:opacity-50 shadow text-sm font-medium transition whitespace-nowrap">
            {saving
              ? <><span className="w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin" /><span className="hidden sm:inline">{isNew ? 'Creating…' : 'Updating…'}</span></>
              : isNew ? <><span>✨</span><span className="hidden sm:inline"> Create Post</span><span className="sm:hidden"> Create</span></> : <><span>💾</span><span className="hidden sm:inline"> Update Post</span><span className="sm:hidden"> Save</span></>
            }
          </button>
        </div>
      </div>

      {/* Save error banner */}
      {saveError && (
        <div className="mb-4 flex items-center gap-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-700 rounded-2xl px-4 py-3">
          <span className="text-red-600 dark:text-red-400 text-sm font-medium">⚠️ {saveError}</span>
          <button onClick={() => setSaveError('')} className="ml-auto text-red-400 hover:text-red-600 transition"><X size={16} /></button>
        </div>
      )}

      {/* Tabs */}
      <div className="flex gap-1 mb-6 bg-gray-100 dark:bg-gray-800 p-1 rounded-xl w-fit">
        {tabs.map(t => (
          <button key={t} onClick={() => setActiveTab(t)}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition ${activeTab === t ? 'bg-white dark:bg-gray-700 shadow text-gray-900 dark:text-gray-100' : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'}`}>
            {tabLabel[t]}
          </button>
        ))}
      </div>

      {/* ── CONTENT TAB ────────────────────────────────────────────── */}
      {activeTab === 'content' && (
        <div className="space-y-6">
          {/* Title + Slug */}
          <div className="bg-white/80 dark:bg-gray-800/80 backdrop-blur-md rounded-2xl shadow-xl border border-gray-200/60 dark:border-gray-700/60 p-6 space-y-4">
            <div>
              <label className={labelCls}>Title (H1) <span className="text-red-500">*</span></label>
              <input value={post.title || ''} onChange={e => { handleTitle(e.target.value); setFieldErrors(f => ({ ...f, title: false })); }} className={`${inputCls} ${fieldErrors.title ? 'border-red-500 dark:border-red-500 ring-1 ring-red-500' : ''}`} placeholder="Post title…" />
            </div>
            <div>
              <label className={labelCls}>Slug <span className="text-gray-400 font-normal text-xs">(URL path)</span> <span className="text-red-500">*</span></label>
              <div className="flex items-center gap-2">
                <span className="text-sm text-gray-400">/blog/</span>
                <input value={post.slug || ''} onChange={e => { setPost(p => ({ ...p, slug: e.target.value })); setFieldErrors(f => ({ ...f, slug: false })); }} className={`${inputCls} flex-1 ${fieldErrors.slug ? 'border-red-500 dark:border-red-500 ring-1 ring-red-500' : ''}`} placeholder="post-slug" />
                <span className="text-sm text-gray-400">/</span>
              </div>
            </div>
          </div>

          {/* Cover Image */}
          <div className="bg-white/80 dark:bg-gray-800/80 backdrop-blur-md rounded-2xl shadow-xl border border-gray-200/60 dark:border-gray-700/60 p-6 space-y-5">
            {/* Desktop cover */}
            <div>
              <label className={labelCls}>🖥️ Cover Image <span className="text-gray-400 font-normal text-xs">(desktop)</span></label>
              <div className="flex gap-2 mb-3">
                <input value={post.cover_image_url || ''} onChange={e => setPost(p => ({ ...p, cover_image_url: e.target.value }))} className={`${inputCls} flex-1`} placeholder="https://…" />
                <button onClick={() => setCoverPickerOpen(true)} className="flex items-center gap-1.5 px-3 py-2 bg-gray-50 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-xl text-sm hover:bg-gray-100 dark:hover:bg-gray-600 transition">
                  <Image size={16} /> Browse
                </button>
              </div>
              {post.cover_image_url && (
                <img src={post.cover_image_url} alt="Cover" className="w-full max-h-48 object-cover rounded-xl border border-gray-200 dark:border-gray-600" />
              )}
            </div>
            {/* Mobile cover */}
            <div>
              <label className={labelCls}>📱 Mobile Cover Image <span className="text-gray-400 font-normal text-xs">(screens ≤ 640px — optional)</span></label>
              <div className="flex gap-2 mb-3">
                <input value={post.mobile_cover_image_url || ''} onChange={e => setPost(p => ({ ...p, mobile_cover_image_url: e.target.value }))} className={`${inputCls} flex-1`} placeholder="https://… (smaller, mobile-optimized image)" />
                <button onClick={() => setMobileCoverPickerOpen(true)} className="flex items-center gap-1.5 px-3 py-2 bg-gray-50 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-xl text-sm hover:bg-gray-100 dark:hover:bg-gray-600 transition">
                  <Image size={16} /> Browse
                </button>
              </div>
              {post.mobile_cover_image_url && (
                <img src={post.mobile_cover_image_url} alt="Mobile Cover" className="max-h-32 object-cover rounded-xl border border-blue-200 dark:border-blue-700" />
              )}
            </div>
          </div>

          {/* Raw body with toolbar */}
          <div className="bg-white/80 dark:bg-gray-800/80 backdrop-blur-md rounded-2xl shadow-xl border border-gray-200/60 dark:border-gray-700/60 p-6">
            <label className={labelCls}>
              Body (Markdown) <span className="text-gray-400 font-normal text-xs ml-2">Optional if using sections below</span>
            </label>
            <MarkdownToolbar
              textareaRef={bodyRef}
              value={post.body || ''}
              onChange={v => setPost(p => ({ ...p, body: v }))}
              onPickImage={() => {
                bodyCursorRef.current = bodyRef.current?.selectionStart ?? (post.body || '').length;
                setBodyImagePickerOpen(true);
              }}
            />
            <textarea
              ref={bodyRef}
              value={post.body || ''}
              onChange={e => setPost(p => ({ ...p, body: e.target.value }))}
              onBlur={e => { bodyCursorRef.current = e.target.selectionStart; }}
              rows={10}
              placeholder="Write your intro here, or leave blank and use the structured sections below…"
              className={`${inputCls} font-mono text-sm`}
            />
          </div>

          {/* Structured Sections */}
          <div className="bg-white/80 dark:bg-gray-800/80 backdrop-blur-md rounded-2xl shadow-xl border border-gray-200/60 dark:border-gray-700/60 p-6 space-y-5">
            <div className="flex items-center justify-between">
              <h2 className="font-semibold text-gray-900 dark:text-gray-100">📄 Content Sections (Paragraph + Image)</h2>
              <button onClick={addSection} className="flex items-center gap-1.5 px-3 py-1.5 bg-blue-50 dark:bg-blue-900/30 border border-blue-200 dark:border-blue-700 rounded-xl text-sm text-blue-700 dark:text-blue-300 hover:bg-blue-100 dark:hover:bg-blue-900/50 transition">
                <Plus size={15} /> Add Section
              </button>
            </div>
            {(post.sections || []).map((sec, i) => (
              <div key={i} className="border border-gray-200 dark:border-gray-700 rounded-xl p-4 space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide">Section {i + 1}</span>
                  {(post.sections || []).length > 1 && (
                    <button onClick={() => removeSection(i)} className="text-red-400 hover:text-red-600 transition">
                      <Trash2 size={15} />
                    </button>
                  )}
                </div>
                <div>
                  <label className={labelCls}>Heading (H2)</label>
                  <input value={sec.heading} onChange={e => updateSection(i, 'heading', e.target.value)} className={inputCls} placeholder="Section heading…" />
                </div>
                <SectionParagraphEditor
                  key={`${loadedAt}-${i}`}
                  body={sec.body}
                  onChange={v => updateSection(i, 'body', v)}
                />
                <div>
                  <label className={labelCls}>Image</label>
                  <div className="flex gap-2 mb-2">
                    <input value={sec.image_url} onChange={e => updateSection(i, 'image_url', e.target.value)} className={`${inputCls} flex-1`} placeholder="https://… or browse below" />
                    <button onClick={() => setSectionImagePicker(i)} className="flex items-center gap-1.5 px-3 py-2 bg-gray-50 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-xl text-sm hover:bg-gray-100 dark:hover:bg-gray-600 transition">
                      <Image size={16} /> Browse
                    </button>
                  </div>
                  {sec.image_url && (
                    <img src={sec.image_url} alt="" className="max-h-32 rounded-lg object-cover border border-gray-200 dark:border-gray-600" />
                  )}
                </div>
              </div>
            ))}
          </div>

          {/* FAQs */}
          <div className="bg-white/80 dark:bg-gray-800/80 backdrop-blur-md rounded-2xl shadow-xl border border-gray-200/60 dark:border-gray-700/60 p-6 space-y-4">
            <div className="flex items-center justify-between">
              <h2 className="font-semibold text-gray-900 dark:text-gray-100">❓ FAQs</h2>
              <button onClick={addFaq} className="flex items-center gap-1.5 px-3 py-1.5 bg-blue-50 dark:bg-blue-900/30 border border-blue-200 dark:border-blue-700 rounded-xl text-sm text-blue-700 dark:text-blue-300 hover:bg-blue-100 dark:hover:bg-blue-900/50 transition">
                <Plus size={15} /> Add FAQ
              </button>
            </div>
            {(post.faqs || []).map((faq, i) => (
              <div key={i} className="border border-gray-200 dark:border-gray-700 rounded-xl p-4 space-y-3">
                <span className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide">FAQ {i + 1}</span>
                <div>
                  <label className={labelCls}>Question (H3)</label>
                  <input value={faq.q} onChange={e => updateFaq(i, 'q', e.target.value)} className={inputCls} placeholder="Question…" />
                </div>
                <div>
                  <label className={labelCls}>Answer</label>
                  <textarea value={faq.a} onChange={e => updateFaq(i, 'a', e.target.value)} rows={3} className={inputCls} placeholder="Answer…" />
                </div>
              </div>
            ))}
          </div>

          {/* Internal Links */}
          <div className="bg-white/80 dark:bg-gray-800/80 backdrop-blur-md rounded-2xl shadow-xl border border-gray-200/60 dark:border-gray-700/60 p-6">
            <div className="flex items-center justify-between mb-3">
              <h2 className="font-semibold text-gray-900 dark:text-gray-100">🔗 Internal Links <span className="text-xs text-gray-400 font-normal ml-1">(up to 5)</span></h2>
              <button onClick={() => setLinkPickerOpen(true)} className="flex items-center gap-1.5 px-3 py-1.5 bg-blue-50 dark:bg-blue-900/30 border border-blue-200 dark:border-blue-700 rounded-xl text-sm text-blue-700 dark:text-blue-300 hover:bg-blue-100 dark:hover:bg-blue-900/50 transition">
                <Link2 size={15} /> Select Posts
              </button>
            </div>
            {(post.internal_links || []).length === 0 ? (
              <p className="text-sm text-gray-400">No internal links selected yet.</p>
            ) : (
              <ul className="space-y-2">
                {(post.internal_links || []).map((link, i) => (
                  <li key={i} className="flex items-center justify-between bg-gray-50 dark:bg-gray-700/40 rounded-xl px-4 py-2.5">
                    <div>
                      <p className="text-sm font-medium text-gray-900 dark:text-gray-100">{link.label}</p>
                      <p className="text-xs text-blue-500">/{link.slug}/</p>
                    </div>
                    <div className="flex items-center gap-2">
                      <a href={`/blog/${link.slug}/`} target="_blank" rel="noreferrer" className="text-gray-400 hover:text-blue-500 transition">
                        <ExternalLink size={14} />
                      </a>
                      <button onClick={() => setPost(p => ({ ...p, internal_links: (p.internal_links || []).filter((_, j) => j !== i) }))} className="text-red-400 hover:text-red-600 transition">
                        <X size={14} />
                      </button>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>

          {/* CTA */}
          <div className="bg-white/80 dark:bg-gray-800/80 backdrop-blur-md rounded-2xl shadow-xl border border-gray-200/60 dark:border-gray-700/60 p-6 space-y-4">
            <h2 className="font-semibold text-gray-900 dark:text-gray-100">🎯 Call to Action</h2>
            <div>
              <label className={labelCls}>CTA Description</label>
              <textarea value={post.cta_text || ''} onChange={e => setPost(p => ({ ...p, cta_text: e.target.value }))} rows={3} className={inputCls} placeholder="Compelling text before the CTA button…" />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className={labelCls}>Button Label</label>
                <input value={post.cta_button_label || ''} onChange={e => setPost(p => ({ ...p, cta_button_label: e.target.value }))} className={inputCls} placeholder="Get a Free Temp Email" />
              </div>
              <div>
                <label className={labelCls}>Button URL</label>
                <input value={post.cta_button_url || ''} onChange={e => setPost(p => ({ ...p, cta_button_url: e.target.value }))} className={inputCls} placeholder="https://inboxme.space/" />
              </div>
            </div>
            {/* Preview */}
            {(post.cta_text || post.cta_button_label) && (
              <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-700 rounded-xl p-4 text-center">
                {post.cta_text && <p className="text-sm text-gray-700 dark:text-gray-300 mb-3">{post.cta_text}</p>}
                {post.cta_button_label && (
                  <span className="inline-block px-5 py-2.5 bg-blue-600 text-white rounded-xl text-sm font-semibold shadow">
                    {post.cta_button_label} →
                  </span>
                )}
                <p className="text-xs text-gray-400 mt-2">CTA preview</p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ── SEO TAB ────────────────────────────────────────────────── */}
      {activeTab === 'seo' && (
        <div className="space-y-6">
          <div className="bg-white/80 dark:bg-gray-800/80 backdrop-blur-md rounded-2xl shadow-xl border border-gray-200/60 dark:border-gray-700/60 p-6 space-y-5">
            <div>
              <label className={labelCls}>
                Meta Description
                <span className="ml-2 text-xs text-gray-400 font-normal">({(post.meta_description || '').length}/160 chars)</span>
              </label>
              <textarea value={post.meta_description || ''} onChange={e => setPost(p => ({ ...p, meta_description: e.target.value }))} rows={3} maxLength={320} className={inputCls} placeholder="Brief description for search engines (150–160 chars)…" />
              {(post.meta_description || '').length > 160 && (
                <p className="text-xs text-orange-500 mt-1">⚠️ Over 160 chars — Google may truncate this</p>
              )}
            </div>
            <div>
              <label className={labelCls}>Meta Keywords <span className="text-gray-400 font-normal text-xs">(comma-separated)</span></label>
              <input value={post.meta_keywords || ''} onChange={e => setPost(p => ({ ...p, meta_keywords: e.target.value }))} className={inputCls} placeholder="temp mail, temporary email, disposable inbox" />
            </div>
            <div>
              <label className={labelCls}>Tags <span className="text-gray-400 font-normal text-xs">(comma-separated)</span></label>
              <input value={tagsInput} onChange={e => setTagsInput(e.target.value)} className={inputCls} placeholder="privacy, temp-mail, how-to" />
              {tagsInput && (
                <div className="flex flex-wrap gap-1.5 mt-2">
                  {tagsInput.split(',').map(t => t.trim()).filter(Boolean).map(t => (
                    <span key={t} className="px-2 py-0.5 bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 rounded-full text-xs">{t}</span>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Open Graph */}
          <div className="bg-white/80 dark:bg-gray-800/80 backdrop-blur-md rounded-2xl shadow-xl border border-gray-200/60 dark:border-gray-700/60 p-6 space-y-4">
            <h2 className="font-semibold text-gray-900 dark:text-gray-100">📣 Open Graph / Social Preview</h2>
            <div>
              <label className={labelCls}>OG Image URL</label>
              <div className="flex gap-2 mb-2">
                <input value={post.meta_og_image || ''} onChange={e => setPost(p => ({ ...p, meta_og_image: e.target.value }))} className={`${inputCls} flex-1`} placeholder="https://… (1200×630 recommended)" />
                <button onClick={() => setOgImagePickerOpen(true)} className="flex items-center gap-1.5 px-3 py-2 bg-gray-50 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-xl text-sm hover:bg-gray-100 dark:hover:bg-gray-600 transition">
                  <Image size={16} /> Browse
                </button>
              </div>
              {post.meta_og_image && (
                <img src={post.meta_og_image} alt="OG" className="max-h-32 rounded-xl object-cover border border-gray-200 dark:border-gray-600" />
              )}
            </div>

            {/* OG Preview Card */}
            <div className="border border-gray-200 dark:border-gray-700 rounded-xl overflow-hidden text-sm">
              <div className="text-xs text-gray-400 px-3 py-1.5 bg-gray-50 dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700">Social preview</div>
              {(post.meta_og_image || post.cover_image_url) && (
                <img src={post.meta_og_image || post.cover_image_url} alt="" className="w-full max-h-36 object-cover" />
              )}
              <div className="p-3">
                <p className="font-semibold text-gray-900 dark:text-gray-100 truncate">{post.title || 'Post Title'}</p>
                <p className="text-gray-500 dark:text-gray-400 text-xs mt-0.5 line-clamp-2">{post.meta_description || 'Meta description will appear here…'}</p>
                <p className="text-gray-400 text-xs mt-1 uppercase">inboxme.space</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── SETTINGS TAB ───────────────────────────────────────────── */}
      {activeTab === 'settings' && (
        <div className="bg-white/80 dark:bg-gray-800/80 backdrop-blur-md rounded-2xl shadow-xl border border-gray-200/60 dark:border-gray-700/60 p-6 space-y-5">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            <div>
              <label className={labelCls}>Author</label>
              <input value={post.author || ''} onChange={e => setPost(p => ({ ...p, author: e.target.value }))} className={inputCls} placeholder="inbox Me" />
            </div>
            <div>
              <label className={labelCls}>Language</label>
              <select value={post.lang || 'en'} onChange={e => setPost(p => ({ ...p, lang: e.target.value as Post['lang'] }))} className={inputCls}>
                <option value="en">🇬🇧 English</option>
                <option value="tr">🇹🇷 Turkish</option>
                <option value="de">🇩🇪 German</option>
                <option value="fr">🇫🇷 French</option>
                <option value="id">🇮🇩 Indonesian</option>
                <option value="pl">🇵🇱 Polish</option>
                <option value="pt">🇧🇷 Portuguese</option>
                <option value="ar">🇸🇦 Arabic</option>
                <option value="hi">🇮🇳 Hindi</option>
                <option value="ur">🇵🇰 Urdu</option>
              </select>
            </div>
            <div>
              <label className={labelCls}>Status</label>
              <select value={post.status || 'draft'} onChange={e => setPost(p => ({ ...p, status: e.target.value as Post['status'] }))} className={inputCls}>
                <option value="draft">Draft</option>
                <option value="scheduled">Scheduled</option>
                <option value="published">Published</option>
                <option value="disabled">Disabled</option>
              </select>
            </div>
            <div>
              <label className={labelCls}>Publish At</label>
              <input type="datetime-local" value={tsToDatetimeLocal(post.publish_at)} onChange={e => setPost(p => ({ ...p, publish_at: datetimeLocalToTs(e.target.value) }))} className={inputCls} />
              <p className="text-xs text-gray-400 mt-1">Required when status is "Scheduled"</p>
            </div>
          </div>
        </div>
      )}

      {/* Modals */}
      {coverPickerOpen && (
        <ImagePickerModal onSelect={url => { setPost(p => ({ ...p, cover_image_url: url })); setCoverPickerOpen(false); }} onClose={() => setCoverPickerOpen(false)} />
      )}
      {mobileCoverPickerOpen && (
        <ImagePickerModal onSelect={url => { setPost(p => ({ ...p, mobile_cover_image_url: url })); setMobileCoverPickerOpen(false); }} onClose={() => setMobileCoverPickerOpen(false)} />
      )}
      {sectionImagePicker !== null && (
        <ImagePickerModal onSelect={url => { updateSection(sectionImagePicker, 'image_url', url); setSectionImagePicker(null); }} onClose={() => setSectionImagePicker(null)} />
      )}
      {ogImagePickerOpen && (
        <ImagePickerModal onSelect={url => { setPost(p => ({ ...p, meta_og_image: url })); setOgImagePickerOpen(false); }} onClose={() => setOgImagePickerOpen(false)} />
      )}
      {bodyImagePickerOpen && (
        <ImagePickerModal
          onSelect={url => {
            const insertText = `\n![Image](${url})\n`;
            const body = post.body || '';
            const pos = bodyCursorRef.current;
            const next = body.slice(0, pos) + insertText + body.slice(pos);
            setPost(p => ({ ...p, body: next }));
            setBodyImagePickerOpen(false);
            setTimeout(() => {
              const el = bodyRef.current;
              if (el) { el.focus(); el.setSelectionRange(pos + insertText.length, pos + insertText.length); }
            }, 50);
          }}
          onClose={() => setBodyImagePickerOpen(false)}
        />
      )}
      {linkPickerOpen && (
        <LinkPickerModal
          selected={post.internal_links || []}
          onDone={links => setPost(p => ({ ...p, internal_links: links }))}
          onClose={() => setLinkPickerOpen(false)}
        />
      )}

      {/* Restore revision confirm */}
      {pendingRestore && (
        <>
          <div className="fixed inset-0 bg-black/50 z-[400]" onClick={() => setPendingRestore(null)} />
          <div className="fixed inset-0 z-[401] flex items-center justify-center p-4">
            <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl p-6 max-w-sm w-full">
              <p className="text-gray-800 dark:text-gray-100 text-sm font-medium mb-1">Restore this revision?</p>
              <p className="text-xs text-gray-400 mb-5">{new Date(pendingRestore.saved_at * 1000).toLocaleString()} · {pendingRestore.status}</p>
              <div className="flex gap-3">
                <button onClick={() => setPendingRestore(null)}
                  className="flex-1 px-4 py-2 rounded-xl border border-gray-300 dark:border-gray-600 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 transition">
                  Cancel
                </button>
                <button onClick={confirmRestore}
                  className="flex-1 px-4 py-2 rounded-xl bg-blue-600 text-white text-sm font-medium hover:bg-blue-700 transition">
                  Restore
                </button>
              </div>
            </div>
          </div>
        </>
      )}

      {/* Revisions drawer */}
      {showRevisions && (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-2xl w-full max-w-lg max-h-[80vh] overflow-y-auto">
            <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
              <h2 className="text-lg font-bold text-gray-900 dark:text-gray-100">Post Revisions</h2>
              <button onClick={() => setShowRevisions(false)} className="p-1 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700"><X size={20} /></button>
            </div>
            <div className="p-4 space-y-3">
              {loadingRevisions && <p className="text-center text-gray-400 py-8">Loading…</p>}
              {!loadingRevisions && revisions.length === 0 && <p className="text-center text-gray-400 py-8">No revisions yet.</p>}
              {revisions.map(rev => (
                <div key={rev.id} className="flex items-start justify-between gap-4 p-4 rounded-xl border border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800">
                  <div>
                    <p className="font-medium text-sm text-gray-900 dark:text-gray-100">{rev.title}</p>
                    <p className="text-xs text-gray-400 mt-0.5">{new Date(rev.saved_at * 1000).toLocaleString()} · {rev.status}</p>
                  </div>
                  <button onClick={() => restore(rev)} className="shrink-0 px-3 py-1.5 text-xs bg-blue-100 text-blue-700 rounded-lg hover:bg-blue-200 dark:bg-blue-900/30 dark:text-blue-400 font-medium">
                    Restore
                  </button>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default BlogEditor;
